import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';

export const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [isHovered, setIsHovered] = useState(false);

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    try {
      await addToCart(product, '125ml', 1);
      toast.success('Added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  // Special rollover images
  const alziCelRolloverImage = "https://customer-assets.emergentagent.com/job_marahhealth/artifacts/sd7ht4oe_image.png";
  const marahCelRolloverImage = "https://customer-assets.emergentagent.com/job_marahhealth/artifacts/ayqty4lr_image.png";
  const rectrailRolloverImage = "https://customer-assets.emergentagent.com/job_marahhealth/artifacts/glyjgeun_image.png";
  const sacSgRolloverImage = "https://customer-assets.emergentagent.com/job_marahhealth/artifacts/kmcm3syg_image.png";
  const dentossoRolloverImage = "https://customer-assets.emergentagent.com/job_marahhealth/artifacts/bpbct54s_image.png";
  const teenCelRolloverImage = "https://customer-assets.emergentagent.com/job_marahhealth/artifacts/mk2i0jg6_image.png";
  const kidsCelRolloverImage = "https://customer-assets.emergentagent.com/job_marahhealth/artifacts/tqsgo7a4_image.png";
  const neoCelRolloverImage = "https://customer-assets.emergentagent.com/job_marahhealth/artifacts/ds85ff4y_image.png";

  return (
    <Card
      className="group cursor-pointer overflow-hidden border-stone-200 hover:border-stone-400 transition-all duration-300 bg-white/50 backdrop-blur-sm"
      onClick={() => navigate(`/product/${product.id}`)}
      data-testid={`product-card-${product.id}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="aspect-square overflow-hidden bg-white flex items-center justify-center p-6 relative">
        {/* Default product image */}
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-contain transition-all duration-500 group-hover:scale-105"
          data-testid="product-image"
        />
        
        {/* ALZI-CEL rollover image - smaller size positioned at bottom */}
        {product.id === 'alzi-cel' && isHovered && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-3/4 bg-white rounded-lg shadow-lg p-2">
            <img
              src={alziCelRolloverImage}
              alt="ALZI-CEL Order Options"
              className="w-full h-auto object-contain"
            />
          </div>
        )}

        {/* MARAH-CEL rollover image - smaller size positioned at bottom */}
        {product.id === 'marah-cel' && isHovered && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-3/4 bg-white rounded-lg shadow-lg p-2">
            <img
              src={marahCelRolloverImage}
              alt="MARAH-CEL Order Options"
              className="w-full h-auto object-contain"
            />
          </div>
        )}

        {/* RECTRAIL rollover image - smaller size positioned at bottom */}
        {product.id === 'rectrail' && isHovered && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-3/4 bg-white rounded-lg shadow-lg p-2">
            <img
              src={rectrailRolloverImage}
              alt="RECTRAIL Order Options"
              className="w-full h-auto object-contain"
            />
          </div>
        )}

        {/* SAC-SG rollover image - smaller size positioned at bottom */}
        {product.id === 'sac-sg' && isHovered && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-3/4 bg-white rounded-lg shadow-lg p-2">
            <img
              src={sacSgRolloverImage}
              alt="SAC-SG Order Options"
              className="w-full h-auto object-contain"
            />
          </div>
        )}

        {/* DENTOSSO rollover image - smaller size positioned at bottom */}
        {product.id === 'dentosso' && isHovered && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-3/4 bg-white rounded-lg shadow-lg p-2">
            <img
              src={dentossoRolloverImage}
              alt="DENTOSSO Order Options"
              className="w-full h-auto object-contain"
            />
          </div>
        )}

        {/* TEEN-CEL rollover image - smaller size positioned at bottom */}
        {product.id === 'teen-cel' && isHovered && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-3/4 bg-white rounded-lg shadow-lg p-2">
            <img
              src={teenCelRolloverImage}
              alt="TEEN-CEL Order Options"
              className="w-full h-auto object-contain"
            />
          </div>
        )}

        {/* KIDS-CEL rollover image - smaller size positioned at bottom */}
        {product.id === 'kids-cel' && isHovered && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-3/4 bg-white rounded-lg shadow-lg p-2">
            <img
              src={kidsCelRolloverImage}
              alt="KIDS-CEL Order Options"
              className="w-full h-auto object-contain"
            />
          </div>
        )}

        {/* NEO-CEL rollover image - smaller size positioned at bottom */}
        {product.id === 'neo-cel' && isHovered && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-3/4 bg-white rounded-lg shadow-lg p-2">
            <img
              src={neoCelRolloverImage}
              alt="NEO-CEL Order Options"
              className="w-full h-auto object-contain"
            />
          </div>
        )}
      </div>
      
      {/* ALZI-CEL description text */}
      {product.id === 'alzi-cel' && (
        <div className="px-4 pb-4 text-center">
          <p className="text-sm text-stone-600">
            Supports brain wellness and memory preservation
          </p>
        </div>
      )}

      {/* MARAH-CEL description text */}
      {product.id === 'marah-cel' && (
        <div className="px-4 pb-4 text-center">
          <p className="text-sm text-stone-600">
            Neutralize Free Radicals, Protect Your Cells
          </p>
        </div>
      )}

      {/* RECTRAIL description text */}
      {product.id === 'rectrail' && (
        <div className="px-4 pb-4 text-center">
          <p className="text-sm text-stone-600">
            Reduces joint pain and inflammation from aging
          </p>
        </div>
      )}

      {/* SAC-SG description text */}
      {product.id === 'sac-sg' && (
        <div className="px-4 pb-4 text-center">
          <p className="text-sm text-stone-600">
            The One Formula Cancer Cells Can't Beat
          </p>
        </div>
      )}

      {/* DENTOSSO description text */}
      {product.id === 'dentosso' && (
        <div className="px-4 pb-4 text-center">
          <p className="text-sm text-stone-600">
            Builds & maintains strong bones and teeth
          </p>
        </div>
      )}

      {/* TEEN-CEL description text */}
      {product.id === 'teen-cel' && (
        <div className="px-4 pb-4 text-center">
          <p className="text-sm text-stone-600">
            Max height potential, Rock-solid immunity
          </p>
        </div>
      )}

      {/* KIDS-CEL description text */}
      {product.id === 'kids-cel' && (
        <div className="px-4 pb-4 text-center">
          <p className="text-sm text-stone-600">
            The Smart Boost Every Growing Kid Needs
          </p>
        </div>
      )}

      {/* NEO-CEL description text */}
      {product.id === 'neo-cel' && (
        <div className="px-4 pb-4 text-center">
          <p className="text-sm text-stone-600">
            Neutralize free radicals, Crush oxidative stress
          </p>
        </div>
      )}
    </Card>
  );
};